import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { AvatarGroup } from '@/shared/components/ui/avatar';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { useMembers } from '@/features/members/public';
import type { Member } from '@/features/members/public';
import { PALETTE } from '@/core/lib/color-palette';
import type { CalendarEventAttendee } from '../types';

const MEMBERS_PAGE_SIZE = 20;

function deterministicColor(seed: string): string {
  const hash = seed.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

interface AttendeesPickerProps {
  attendees: CalendarEventAttendee[];
  /** Full replacement list — built from real member data, never placeholder ids. */
  onChange: (attendees: CalendarEventAttendee[]) => void;
  disabled?: boolean;
}

export function AttendeesPicker({ attendees, onChange, disabled = false }: AttendeesPickerProps) {
  const { t } = useTranslation('calendar');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageSize, setPageSize] = useState(MEMBERS_PAGE_SIZE);

  const {
    data: knownMembers,
    totalCount,
    isLoading,
    isFetching,
  } = useMembers(debouncedSearch, 0, pageSize);

  const addedUserIds = new Set(attendees.map((a) => a.userId).filter((id): id is string => !!id));
  const selectableMembers = knownMembers.filter((m) => !addedUserIds.has(m.userId));
  const hasMore = knownMembers.length < totalCount;

  function handleListScroll(e: React.UIEvent<HTMLDivElement>) {
    if (!hasMore || isFetching) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setPageSize((n) => n + MEMBERS_PAGE_SIZE);
    }
  }

  // Takes the clicked `Member` directly — no re-lookup by id against
  // `knownMembers`, which could silently miss (and no-op) if that array had
  // already moved on to a different page/query by the time this runs.
  function addMember(member: Member) {
    onChange([
      ...attendees,
      {
        userId: member.userId,
        email: member.email,
        displayName: member.displayName,
        isOrganizer: false,
        isOptional: false,
        responseStatus: 'needsaction',
      },
    ]);
    setSearch('');
  }

  function removeMember(userId: string) {
    onChange(attendees.filter((a) => a.userId !== userId));
  }

  const namesLabel = (() => {
    if (attendees.length === 0) return null;
    const [first, second, ...rest] = attendees;
    const names = [first?.displayName, second?.displayName].filter(Boolean);
    const label = names.join(', ');
    return rest.length > 0
      ? t('eventFicha.attendees.more', { names: label, count: rest.length })
      : label;
  })();

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="calendar-attendees-picker">
      {attendees.length > 0 && (
        <>
          <AvatarGroup>
            {attendees.map((a) => (
              <Tooltip key={a.userId ?? a.email}>
                <TooltipTrigger asChild>
                  <div className="group relative">
                    <UserAvatar
                      name={a.displayName}
                      color={deterministicColor(a.userId ?? a.email)}
                      size="sm"
                    />
                    {!disabled && a.userId && (
                      <button
                        type="button"
                        onClick={() => removeMember(a.userId!)}
                        aria-label={t('eventFicha.attendees.remove', { name: a.displayName })}
                        data-testid={`calendar-attendee-remove-${a.userId}`}
                        className="absolute -top-1 -right-1 hidden size-3.5 items-center justify-center rounded-full bg-destructive text-white group-hover:flex"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>{a.displayName}</TooltipContent>
              </Tooltip>
            ))}
          </AvatarGroup>
          <span className="truncate text-sm text-muted-foreground">{namesLabel}</span>
        </>
      )}

      {!disabled && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 shrink-0 rounded-full"
              data-testid="calendar-attendees-picker-trigger"
            >
              <Plus className="h-3.5 w-3.5" />
              {attendees.length === 0 && t('eventFicha.attendees.add')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2" align="start" onClick={(e) => e.stopPropagation()}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('eventFicha.attendees.searchPlaceholder')}
              className="h-8 text-xs mb-2"
              data-testid="calendar-attendees-picker-search"
            />
            <Separator className="mb-1" />
            <div className="max-h-48 overflow-y-auto" onScroll={handleListScroll}>
              {isLoading ? (
                <div className="flex flex-col gap-1.5 px-1 py-1.5">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-6 w-full rounded" />
                  ))}
                </div>
              ) : selectableMembers.length === 0 ? (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  {t('eventFicha.attendees.noResults')}
                </p>
              ) : (
                <>
                  {selectableMembers.map((m) => (
                    <button
                      type="button"
                      key={m.userId}
                      onClick={() => addMember(m)}
                      className="flex w-full items-center gap-2 rounded px-2 py-1.5 hover:bg-accent"
                      data-testid={`calendar-attendees-picker-member-${m.userId}`}
                    >
                      <UserAvatar
                        name={m.displayName}
                        color={deterministicColor(m.userId)}
                        src={m.avatarUrl ?? undefined}
                        size="xs"
                      />
                      <span className="flex-1 truncate text-left text-xs">{m.displayName}</span>
                    </button>
                  ))}
                  {isFetching && (
                    <div className="flex justify-center py-1.5">
                      <Skeleton className="h-3 w-16 rounded" />
                    </div>
                  )}
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {disabled && attendees.length === 0 && (
        <span className="text-sm text-muted-foreground">{t('eventFicha.attendees.none')}</span>
      )}
    </div>
  );
}
